// @flow

import { RestClientAuthenticated } from '../network';
import { turanBaseURL } from '../../utils/urlUtils';
import type {
  RoiRequest,
  RoiSuggestionRequest,
  RoiCustomFocusBean,
} from '../../types/apolloType';

const storesApi = {
  fetchStoresResults: (
    codstr: string,
    bl1Id: ?number,
    bl2Id: ?number,
    bl3Id: ?number,
    selectedBL: ?number[],
    location: ?string,
    internationalRetailer: boolean,
    equivalentGla: boolean,
    pcCodeList: string[],
    countryCodeList: string[],
    mustBeFlagship: boolean,
    scCategory: ?string,
    mustBeWestfield: boolean,
    su: boolean,
    msu: boolean,
    lu: boolean,
    allBl: boolean,
    insideSc: boolean,
  ) =>
    new RestClientAuthenticated(`/api/apollo/compute/${codstr}`, 'GET')
      .addParam('international', String(internationalRetailer))
      .addParam('bl1id', bl1Id)
      .addParam('bl2id', bl2Id)
      .addParam('bl3id', bl3Id)
      .addParamList('selectedBL', selectedBL)
      .addParam('equivalentGla', equivalentGla)
      .addParam('mustBeFlagship', mustBeFlagship)
      .addParam('scCategory', scCategory)
      .addParam('mustBeWestfield', mustBeWestfield)
      .addParamList('pcCode', pcCodeList)
      .addParamList('countryCode', countryCodeList)
      .addParam('su', su)
      .addParam('msu', msu)
      .addParam('lu', lu)
      .addParam('insideSc', insideSc)
      .addParam('location', location)
      .addParam('allBl', allBl)
      .execute(),
  computeRoiForRetailerAndStore: (
    codstr: string,
    roiRequest: RoiRequest,
  ) =>
    new RestClientAuthenticated(`/api/apollo/compute/${codstr}/roi`, 'POST')
      .jsonBody(roiRequest)
      .execute(),
  computeRoiSuggestion: (
    codstr: string,
    roiSuggestionRequest: RoiSuggestionRequest,
    focusableKpi: string,
  ) =>
    new RestClientAuthenticated(`/api/apollo/compute/${codstr}/roi/suggestion`, 'POST')
      .jsonBody(roiSuggestionRequest)
      .addParam('focusableKpi', focusableKpi)
      .execute(),
  computeCustomFocusRoi: (
    codstr: string,
    roiCustomFocusBean: RoiCustomFocusBean,
  ) =>
    new RestClientAuthenticated(`/api/apollo/compute/${codstr}/roi/custom-focus`, 'POST')
      .jsonBody(roiCustomFocusBean)
      .execute(),
  fetchCustomRetailerResults: (
    retailerId: string,
    codeStr: number,
  ) =>
    new RestClientAuthenticated(`/api/apollo/compute/${codeStr}/${retailerId}`, 'GET').execute(),
  fetchMainStore: (codstr: string) => new RestClientAuthenticated(`/api/apollo/store/${codstr}`, 'GET').execute(),
  fetchAvailableRetailers: (codstr: string) => new RestClientAuthenticated(`/api/apollo/compute/${codstr}/available`, 'GET').execute(),
  fetchStoresSources: (searchCriterias: any, idRetailer: number, isCustom: boolean) =>
    new RestClientAuthenticated(`/api/apollo/stores/${searchCriterias.codstr}/${idRetailer}`, 'GET')
      .addParam('international', String(searchCriterias.internationalRetailer))
      .addParam('bl1id', searchCriterias.bl1id)
      .addParam('bl2id', searchCriterias.bl2id)
      .addParamList('bl3id', searchCriterias.bl3id)
      .addParamList('selectedBL', searchCriterias.selectedBL)
      .addParam('insideSc', searchCriterias.insideSc)
      .addParam('location', searchCriterias.location)
      .addParam('equivalentGla', searchCriterias.equivalentGla)
      .addParam('scCategory', searchCriterias.scCategory)
      .addParam('isCustom', isCustom)
      .addParam('mustBeFlagship', searchCriterias.mustBeFlagship)
      .addParam('mustBeWestfield', searchCriterias.mustBeWestfield)
      .addParam('allBl', searchCriterias.allBl)
      .addParamList('pcCode', searchCriterias.pcCodeList)
      .addParamList('countryCode', searchCriterias.countryCodeList)
      .addParam('su', searchCriterias.su)
      .addParam('msu', searchCriterias.msu)
      .addParam('lu', searchCriterias.lu)
      .execute(),
  generateM30: (searchCriterias: any, idRetailer: number, isCustom: boolean) =>
    new RestClientAuthenticated('/api/report/m30', 'POST')
      .addParam('codstr', String(searchCriterias.codstr))
      .addParam('retailerId', String(idRetailer))
      .addParam('international', String(searchCriterias.internationalRetailer))
      .addParam('bl1id', searchCriterias.bl1id)
      .addParam('bl2id', searchCriterias.bl2id)
      .addParamList('bl3id', searchCriterias.bl3id)
      .addParamList('selectedBL', searchCriterias.selectedBL)
      .addParam('insideSc', searchCriterias.insideSc)
      .addParam('location', searchCriterias.location)
      .addParam('equivalentGla', searchCriterias.equivalentGla)
      .addParam('scCategory', searchCriterias.scCategory)
      .addParam('isCustom', isCustom)
      .addParam('mustBeFlagship', searchCriterias.mustBeFlagship)
      .addParam('mustBeWestfield', searchCriterias.mustBeWestfield)
      .addParam('allBl', searchCriterias.allBl)
      .addParamList('pcCode', searchCriterias.pcCodeList)
      .addParamList('countryCode', searchCriterias.countryCodeList)
      .addParam('su', searchCriterias.su)
      .addParam('msu', searchCriterias.msu)
      .addParam('lu', searchCriterias.lu)
      .execute(),
  generateGoodwill: (pcId: string, codstr: string) =>
    new RestClientAuthenticated(`${turanBaseURL()}/report/${pcId}/goodwill/${codstr}`, 'GET').execute(),
  generateGoodwillFromBestRetailer: (
    pcId: number,
    codstr: number,
    oldCodstr: number,
    retailerId: number,
    retailerName: string,
    bl3Name: string,
    glaExclSst: number,
    mgr: number,
    salesR12M: number,
    trv: number,
  ) =>
    new RestClientAuthenticated(`${turanBaseURL()}/report/${pcId}/goodwill/${codstr}/best-retailer`, 'GET')
      .addParam('oldCodstr', oldCodstr)
      .addParam('retailerId', retailerId)
      .addParam('retailerName', retailerName)
      .addParam('bl3Name', bl3Name)
      .addParam('glaExclSst', glaExclSst)
      .addParam('mgr', mgr)
      .addParam('salesR12M', salesR12M)
      .addParam('trv', trv)
      .execute(),
  fetchAllBranchLevel: () =>
    new RestClientAuthenticated('/api/apollo/branch-level', 'GET').execute(),
  fetchTopRetailers: () =>
    new RestClientAuthenticated(`${turanBaseURL()}/gemini/top-retailers`, 'GET').execute(),
  fetchRetailerWeightedSpend: (pcId: string, branchName: string) =>
    new RestClientAuthenticated(`${turanBaseURL()}/marketing/shopping-centers/${pcId}/top-weighted-spend`, 'GET')
      .addParam('branchName', branchName)
      .execute(),
  generateWeightedSpendReport: (pcId: string) =>
    new RestClientAuthenticated(`${turanBaseURL()}/marketing/shopping-centers/${pcId}/top-weighted-spend/report`, 'GET')
      .execute(),
  fetchRetailerGoodwill: (
    pcId: number,
    codstr: number,
    oldCodstr: number,
    retailerId: number,
    retailerName: string,
    bl3Name: string,
    glaExclSst: number,
    mgr: number,
    salesR12M: number,
    trv: number,
  ) =>
    new RestClientAuthenticated(`${turanBaseURL()}/retailers/goodwill/${codstr}`, 'GET')
      .addParam('pcId', pcId)
      .addParam('oldCodstr', oldCodstr)
      .addParam('retailerId', retailerId)
      .addParam('retailerName', retailerName)
      .addParam('bl3Name', bl3Name)
      .addParam('glaExclSst', glaExclSst)
      .addParam('mgr', mgr)
      .addParam('salesR12M', salesR12M)
      .addParam('trv', trv)
      .execute(),
};

export default storesApi;
